import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ExternalLink, GitBranch, Play, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useMeshPeers } from '../widgets/NodePicker';
import { nodeDisplayLabel, peerMatchesKey } from '../widgets/node-targets';
import type { WidgetRenderProps } from '../widgets/types';
import { Alert } from './ui/Alert';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';

interface PlanNode {
  target: string;
  reachable: boolean;
  stable_id: string | null;
  label: string | null;
  hostname: string | null;
  server_env: string | null;
  edition: string | null;
  role: string | null;
  current_commit: string | null;
  origin_project: string | null;
  target_available: boolean;
  update_direction: string | null;
  error: string | null;
}

interface RolloutPlan {
  ok: boolean;
  error?: string;
  version: string;
  commit: { id: string; short_id: string; title: string; committed_date: string | null };
  artifact_ready: boolean;
  targets: string[];
  nodes: PlanNode[];
  production_count: number;
  blockers: string[];
  ready: boolean;
}

interface RolloutStart {
  ok: boolean;
  error?: string;
  pipeline?: { id: number; status: string; web_url?: string };
  plan?: RolloutPlan;
}

interface RolloutStatus {
  ok: boolean;
  error?: string;
  pipeline?: { id: number; status: string; web_url?: string; sha?: string };
  job?: { id: number; status: string; web_url?: string } | null;
}

const TERMINAL = new Set(['success', 'failed', 'canceled', 'skipped']);

function shortSha(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '—';
}

export function FleetRolloutCard({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const peers = useMeshPeers({ includeLocal: true });
  const [version, setVersion] = useState('master');
  const [targets, setTargets] = useState<string[]>([]);
  const [plan, setPlan] = useState<RolloutPlan | null>(null);
  const [includeProduction, setIncludeProduction] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pipelineId, setPipelineId] = useState<number | null>(null);
  const initialized = useRef(false);

  const options = useMemo(() => {
    const rows = Array.isArray(peers.data) ? peers.data : [];
    const local = rows.find((peer) => peer.is_local);
    const out = [{
      target: 'local',
      label: local ? nodeDisplayLabel(local) : t('toolbox.rollout.local', 'Tento node'),
      env: local?.env ?? null,
      enabled: true,
    }];
    const seen = new Set(['local']);
    for (const peer of rows) {
      if (peer.is_local) continue;
      const target = peer.stable_id?.trim() ?? '';
      if (!target || seen.has(target)) continue;
      seen.add(target);
      out.push({
        target,
        label: nodeDisplayLabel(peer),
        env: peer.env ?? null,
        enabled: peer.enabled !== false,
      });
    }
    return out;
  }, [peers.data, t]);

  useEffect(() => {
    if (initialized.current || peers.isLoading) return;
    initialized.current = true;
    if (!scope.node) {
      setTargets(['local']);
      return;
    }
    const peer = (peers.data ?? []).find((item) => peerMatchesKey(item, scope.node));
    setTargets([peer?.stable_id || scope.node]);
  }, [peers.data, peers.isLoading, scope.node]);

  const invalidatePlan = () => {
    setPlan(null);
    setIncludeProduction(false);
  };

  const planMutation = useMutation({
    mutationFn: () =>
      api.mcp<RolloutPlan>(
        'toolbox.rollout_plan',
        { version: version.trim(), targets },
        { hostLocal: true, label: t('toolbox.rollout.planning', 'Ověřuji rollout') },
      ),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error ?? t('toolbox.rollout.planFailed', 'Plán rolloutu selhal'));
        setPlan(null);
        return;
      }
      setPlan(result);
      setIncludeProduction(false);
      if (result.ready) toast.success(t('toolbox.rollout.planReady', 'Rollout plán je připravený'));
    },
    onError: (error) => toast.error((error as Error).message),
  });

  const startMutation = useMutation({
    mutationFn: () =>
      api.mcp<RolloutStart>(
        'toolbox.rollout_start',
        { version: version.trim(), targets, include_production: includeProduction },
        { hostLocal: true, label: t('toolbox.rollout.starting', 'Spouštím rollout') },
      ),
    onSuccess: (result) => {
      if (!result.ok || !result.pipeline?.id) {
        toast.error(result.error ?? t('toolbox.rollout.startFailed', 'Rollout se nespustil'));
        if (result.plan) setPlan(result.plan);
        return;
      }
      setPipelineId(result.pipeline.id);
      setConfirmOpen(false);
      toast.success(t('toolbox.rollout.started', 'Externí rollout controller byl spuštěn'));
    },
    onError: (error) => toast.error((error as Error).message),
  });

  const status = useQuery({
    queryKey: ['toolbox-rollout-status', pipelineId],
    enabled: pipelineId !== null,
    queryFn: () =>
      api.mcp<RolloutStatus>(
        'toolbox.rollout_status',
        { pipeline_id: pipelineId },
        { hostLocal: true, silent: true },
      ),
    refetchInterval: (query) => {
      const state = query.state.data?.pipeline?.status;
      return state && TERMINAL.has(state) ? false : 5_000;
    },
  });

  const toggleTarget = (target: string) => {
    setTargets((current) =>
      current.includes(target) ? current.filter((item) => item !== target) : [...current, target]
    );
    invalidatePlan();
  };
  const pipelineStatus = status.data?.job?.status ?? status.data?.pipeline?.status ?? null;
  const productionApproved = !plan?.production_count || includeProduction;

  return (
    <div className='space-y-4'>
      <div>
        <div className='flex items-center gap-2 text-sm font-semibold text-gray-100'>
          <GitBranch size={16} />
          {t('toolbox.rollout.title', 'Řízené nasazení ACM')}
        </div>
        <p className='mt-1 text-xs text-gray-400'>
          {t(
            'toolbox.rollout.subtitle',
            'Vyber verzi a explicitní nody. Před první změnou se ověří zdroj, prostředí, edice i CI artefakt.',
          )}
        </p>
      </div>

      <div className='grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end'>
        <label className='block'>
          <span className='mb-1 block text-xs font-medium text-gray-400'>
            {t('toolbox.rollout.version', 'Verze / tag / commit')}
          </span>
          <Input
            value={version}
            onChange={(event) => {
              setVersion(event.target.value);
              invalidatePlan();
            }}
            placeholder='master nebo release/maxcloud/v2.1.0'
          />
        </label>
        <Button
          size='md'
          variant='primary'
          icon={<RefreshCw size={14} />}
          loading={planMutation.isPending}
          disabled={!version.trim() || targets.length === 0}
          onClick={() => planMutation.mutate()}
        >
          {t('toolbox.rollout.plan', 'Ověřit plán')}
        </Button>
      </div>

      <div>
        <div className='mb-2 flex items-center justify-between'>
          <span className='text-xs font-medium text-gray-400'>
            {t('toolbox.rollout.targets', 'Cílové nody')}
          </span>
          <span className='text-xs text-gray-500'>{targets.length} vybráno</span>
        </div>
        <div className='grid gap-2 md:grid-cols-2 xl:grid-cols-3'>
          {options.map((node) => (
            <label
              key={node.target}
              className={`flex cursor-pointer items-start gap-2 rounded-xl border p-3 transition-colors ${
                targets.includes(node.target)
                  ? 'border-plex/50 bg-plex/10'
                  : 'border-gray-700/70 bg-gray-900/30 hover:border-gray-600'
              }`}
            >
              <input
                type='checkbox'
                className='mt-0.5 accent-blue-500'
                checked={targets.includes(node.target)}
                disabled={!node.enabled}
                onChange={() => toggleTarget(node.target)}
              />
              <span className='min-w-0'>
                <span className='block truncate text-sm text-gray-100'>{node.label}</span>
                <span className='block truncate font-mono text-[10px] text-gray-500'>
                  {node.target}
                </span>
              </span>
              {node.env && (
                <Badge variant={node.env === 'production' ? 'error' : 'default'}>{node.env}</Badge>
              )}
            </label>
          ))}
        </div>
        {peers.isError && (
          <p className='mt-2 text-xs text-red-400'>
            {t('toolbox.rollout.peersFailed', 'Seznam vzdálených nodů se nepodařilo načíst.')}
          </p>
        )}
      </div>

      {plan && (
        <div className='space-y-3 rounded-xl border border-gray-700/70 bg-gray-900/35 p-3'>
          <div className='flex flex-wrap items-center gap-2'>
            <Badge variant={plan.ready ? 'success' : 'error'}>
              {plan.ready ? 'READY' : 'BLOCKED'}
            </Badge>
            <span className='font-mono text-xs text-gray-300'>{plan.commit.short_id}</span>
            <span className='min-w-0 truncate text-xs text-gray-400'>{plan.commit.title}</span>
            <Badge variant={plan.artifact_ready ? 'success' : 'error'}>
              CI artifact {plan.artifact_ready ? 'OK' : 'missing'}
            </Badge>
          </div>

          <div className='overflow-x-auto'>
            <table className='w-full min-w-[720px] text-left text-xs'>
              <thead className='text-gray-500'>
                <tr>
                  <th className='px-2 py-1.5'>Node</th>
                  <th className='px-2 py-1.5'>Env</th>
                  <th className='px-2 py-1.5'>{t('fleetrolloutcard.edice', 'Edice')}</th>
                  <th className='px-2 py-1.5'>{t('fleetrolloutcard.zdroj', 'Zdroj')}</th>
                  <th className='px-2 py-1.5'>{t('fleetrolloutcard.aktualniSha', 'Aktuální SHA')}</th>
                  <th className='px-2 py-1.5'>{t('fleetrolloutcard.smer', 'Směr')}</th>
                  <th className='px-2 py-1.5'>Preflight</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-800'>
                {plan.nodes.map((node) => (
                  <tr key={node.target}>
                    <td className='px-2 py-2 text-gray-200'>{node.label ?? node.target}</td>
                    <td className='px-2 py-2'>
                      <Badge variant={node.server_env === 'production' ? 'error' : 'default'}>
                        {node.server_env ?? 'unknown'}
                      </Badge>
                    </td>
                    <td className='px-2 py-2 text-gray-300'>{node.edition ?? '—'}</td>
                    <td className='px-2 py-2 text-gray-400'>{node.origin_project ?? '—'}</td>
                    <td className='px-2 py-2 font-mono text-gray-400'>
                      {shortSha(node.current_commit)}
                    </td>
                    <td className='px-2 py-2 text-gray-400'>
                      {node.update_direction ?? '—'}
                    </td>
                    <td className='px-2 py-2'>
                      <Badge variant={node.error ? 'error' : 'success'}>
                        {node.error ?? 'OK'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {plan.blockers.length > 0 && (
            <Alert variant='destructive'>
              <div className='text-xs font-semibold'>Blokery</div>
              <ul className='mt-1 list-disc space-y-1 pl-4 text-xs'>
                {plan.blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}
              </ul>
            </Alert>
          )}

          {plan.production_count > 0 && (
            <Alert variant='warning'>
              <label className='flex cursor-pointer items-start gap-2 text-sm'>
                <input
                  type='checkbox'
                  className='mt-0.5 accent-amber-500'
                  checked={includeProduction}
                  onChange={(event) => setIncludeProduction(event.target.checked)}
                />
                <span>
                  {t(
                    'toolbox.rollout.productionConsent',
                    'Plán obsahuje {{count}} produkční node. Výslovně povoluji zahrnutí produkce.',
                    { count: plan.production_count },
                  )}
                </span>
              </label>
            </Alert>
          )}

          <Button
            size='md'
            variant={plan.production_count ? 'warning' : 'success'}
            icon={<Play size={14} />}
            disabled={!plan.ready || !productionApproved || pipelineId !== null}
            onClick={() => setConfirmOpen(true)}
          >
            {t('toolbox.rollout.deploy', 'Spustit rollout')}
          </Button>
        </div>
      )}

      {pipelineId !== null && (
        <Alert
          variant={pipelineStatus === 'success'
            ? 'success'
            : pipelineStatus && TERMINAL.has(pipelineStatus)
            ? 'destructive'
            : 'info'}
        >
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-sm font-semibold'>Pipeline #{pipelineId}</span>
            <Badge
              variant={pipelineStatus === 'success'
                ? 'success'
                : pipelineStatus && TERMINAL.has(pipelineStatus)
                ? 'error'
                : 'info'}
            >
              {pipelineStatus ?? 'created'}
            </Badge>
            {status.data?.pipeline?.web_url && (
              <a
                href={status.data.pipeline.web_url}
                target='_blank'
                rel='noreferrer'
                className='inline-flex items-center gap-1 text-xs text-plex hover:underline'
              >
                GitLab <ExternalLink size={11} />
              </a>
            )}
          </div>
          {status.data?.error && <div className='mt-1 text-xs'>{status.data.error}</div>}
        </Alert>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t('toolbox.rollout.confirmTitle', 'Potvrdit řízený rollout')}
      >
        <div className='space-y-4'>
          <p className='text-sm text-gray-300'>
            {t('fleetrolloutcard.nasadit', 'Nasadit')} <span className='font-mono text-gray-100'>{plan?.commit.id}</span> na{' '}
            <strong>{targets.length}</strong> {t('fleetrolloutcard.explicitnichNodu', 'explicitních nodů?')}
          </p>
          <p className='text-xs text-gray-400'>
            {t('fleetrolloutcard.controllerPredPrvniZmenou', 'Controller před první změnou znovu ověří všechny nody a pak je aktualizuje postupně.')}
          </p>
          <div className='flex justify-end gap-2'>
            <Button variant='secondary' onClick={() => setConfirmOpen(false)}>
              {t('common.cancel', 'Zrušit')}
            </Button>
            <Button
              variant={plan?.production_count ? 'warning' : 'success'}
              icon={<Play size={13} />}
              loading={startMutation.isPending}
              onClick={() => startMutation.mutate()}
            >
              {t('toolbox.rollout.confirmDeploy', 'Ano, nasadit')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
